import api from './api';

export const applicationService = {
  // 1. Apply for ex-gratia application
  // POST /api/exgratia/apply
  applyExgratia: async (applicationData, images = []) => {
    const formData = new FormData();
    
    formData.append('data', JSON.stringify(applicationData));
    
    images.forEach((image, index) => {
      formData.append('images', image);
    });
    
    const response = await api.post('/exgratia/apply', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // 2. Get application by reference number or other identifier
  // GET /api/exgratia/application/<get_by>/<application_refno>
  getApplicationByRef: async (getBy, applicationRefno) => {
    const response = await api.get(`/exgratia/application/${getBy}/${applicationRefno}`);
    return response.data;
  },

  // 3. Get all applications with filters and pagination
  // GET /api/exgratia/applications
  getApplications: async (params = {}) => {
    const response = await api.get('/exgratia/applications', { 
      params: {
        status: params.status || '',
        page: params.page || 1,
        per_page: params.per_page || 10,
        ...params
      }
    });
    return response.data;
  },

  // 4. Get current user's applications
  // GET /api/exgratia/user/applications
  getUserApplications: async (params = {}) => {
    const response = await api.get('/exgratia/user/applications', { 
      params: {
        page: params.page || 1,
        per_page: params.per_page || 10,
        search: params.search || '',
        status: params.status || '',
        ...params
      }
    });
    return response.data;
  },

  // 5. Edit application by reference number
  // POST /api/exgratia/applications/edit/<application_refno>
  editApplication: async (applicationRefno, applicationData) => {
    const response = await api.post(`/exgratia/applications/edit/${applicationRefno}`, applicationData);
    return response.data;
  },

  // 6. Print Full report of an application
  // GET /api/exgratia-report/generate_pdf/<application_id>
  downloadApplFullReport: async (applicationId) => {
    const response = await api.get(`/exgratia-report/generate_pdf/${applicationId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Legacy compatibility methods (keeping existing functionality)
  
  // Get single application by ID (for backward compatibility)
  getApplication: async (id) => {
    const response = await api.get(`/exgratia/application/id/${id}`);
    return response.data;
  },

  // Create new application (alias for applyExgratia)
  createApplication: async (applicationData) => {
    return this.applyExgratia(applicationData);
  },

  // Update application (alias for editApplication when using ID)
  updateApplication: async (id, applicationData) => {
    // If id looks like a reference number (format: SK2025MN0001), use edit endpoint
    if (typeof id === 'string' && /^[A-Z]{2}\d{4}[A-Z]{2}\d{4}$/.test(id)) {
      return this.editApplication(id, applicationData);
    }
    // Otherwise, try to get ref number first then edit
    const app = await this.getApplication(id);
    return this.editApplication(app.application_refno, applicationData);
  },

  // Get current user's applications (alias for getUserApplications)
  getMyApplications: async (params = {}) => {
    return this.getUserApplications(params);
  },

  // Workflow and additional functionality

  // Delete application
  deleteApplication: async (id) => {
    const response = await api.delete(`/exgratia/applications/${id}`);
    return response.data;
  },

  // Assign application to SDM
  assignApplicationToSDM: async (id, workflowData) => {
    const response = await api.post(`/workflow/process/${id}/assign_to_sdm`, workflowData);
    return response.data;
  },

  initiateDamageInspection: async (id, inspectionData) => {
    const response = await api.post(`/workflow/process/${id}/damage_inspection`, inspectionData);
    return response.data;
  },

  assignFieldInspectionOfficial: async (id, assignmentData) => {
    const response = await api.post(`/workflow/process/${id}/field_inspection`, assignmentData);
    return response.data;
  },

  initiateExgratiaCalculation: async (id, calculationData) => {
    const response = await api.post(`/workflow/process/${id}/initiate_exgratia_calculation`, calculationData);
    return response.data;
  },

  updateExgratiaAmount: async (id, updatedData) => {
    const response = await api.post(`/workflow/update-exgratia-amount/${id}`, updatedData);
    return response.data;
  },

  //Forward application to DPO in workflow
  forwardApplicationToDPO: async (id, workflowData) => {
    const response = await api.post(`/workflow/process/${id}/forward_to_dpo`, workflowData);
    return response.data;
  },

  // Forward application to dc in workflow
  forwardApplicationToDC: async (id, workflowData) => {
    const response = await api.post(`/workflow/process/${id}/forward_to_dc`, workflowData);
    return response.data;
  },

  //Forward application to accounts in workflow
  forwardApplicationToAccounts: async (id, workflowData) => {
    const response = await api.post(`/workflow/process/${id}/forward_to_accounts`, workflowData);
    return response.data;
  },

  // Cheque details entry in workflow
  chequeEntry: async (id, chequeData) => {
    const response = await api.post(`/workflow/process/${id}/cheque_entry`, chequeData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Disburse cheque in workflow
  disburseCheque: async (id, disburseData) => {
    const response = await api.post(`/workflow/process/${id}/disburse_cheque`, disburseData);
    return response.data;
  },

  // Backward application in workflow
  backwardApplicationForReview: async (id, workflowData) => {
    const response = await api.post(`/workflow/process/${id}/backward_for_review`, workflowData);
    return response.data;
  },

  // Reject application
  rejectApplication: async (id, rejectionData) => {
    const response = await api.post(`/workflow/process/${id}/reject_application`, rejectionData);
    return response.data;
  },

  // Get application workflow history
  getWorkflowHistory: async (id) => {
    const response = await api.get(`/workflow/history/${id}`);
    return response.data;
  },

  // Get application statistics
  getApplicationStats: async (params = {}) => {
    const response = await api.get('/exgratia/applications/stats', { params });
    return response.data;
  },

  // Export applications
  exportApplications: async (params = {}) => {
    const response = await api.get('/exgratia/applications/export', {
      params,
      responseType: 'blob',
    });
    return response;
  },

  // Bulk update applications
  bulkUpdateApplications: async (ids, updateData) => {
    const response = await api.post('/exgratia/applications/bulk-update', {
      ids,
      updateData,
    });
    return response.data;
  },

  // Helper method to validate application data
  validateApplicationData: (data) => {
    const requiredFields = [
      'applicant_name',
      'sodowo',
      'village',
      'ward_id',
      'land_khatian_number',
      'land_plot_nos',
      'ph_number',
      'voter_id',
      'damage_type',
      'remarks',
      'actual_nc_datetime'
    ];

    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // // Validate datetime format
    // const datetimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    // if (!datetimeRegex.test(data.actual_nc_datetime)) {
    //   throw new Error('actual_nc_datetime must be in format YYYY-MM-DD HH:MM:SS');
    // }

    return true;
  },
};
